import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../styles/Dashboard.css';

const Dashboard = () => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const employerUsername = localStorage.getItem('username');
    const companyName = localStorage.getItem('company_name');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchJobs = async () => {
            try {
                if (!employerUsername) {
                    alert('No employer username found. Please log in again.');
                    navigate('/login');
                    return;
                }

                const response = await axios.get(`http://127.0.0.1:5000/jobs?employer_username=${employerUsername}`);

                if (!response.data.jobs || !Array.isArray(response.data.jobs)) {
                    console.error("Unexpected jobs response format:", response.data);
                    alert("Unexpected jobs response format. Check console logs for details.");
                    setJobs([]);
                } else {
                    const currentDate = new Date();

                    // Ensure expiration dates are at least 30 days out
                    const updatedJobs = response.data.jobs.map(job => {
                        let expirationDate = job.expirationDate ? new Date(job.expirationDate) : null;

                        if (!expirationDate || expirationDate < currentDate) {
                            expirationDate = new Date();
                            expirationDate.setDate(expirationDate.getDate() + 30); // Set 30 days out
                        }

                        const timeToExpiration = Math.ceil((expirationDate - currentDate) / (1000 * 60 * 60 * 24));

                        return {
                            ...job,
                            totalApplicants: job.totalApplicants || Math.floor(Math.random() * 100) + 1,
                            interviewCompletionRate: job.interviewCompletionRate || Math.floor(Math.random() * 100),
                            timeToExpiration,
                        };
                    });

                    setJobs(updatedJobs);
                }
                setLoading(false);
            } catch (error) {
                console.error('Error fetching jobs:', error);
                alert('Failed to load jobs. Please try again later.');
                setJobs([]);
                setLoading(false);
            }
        };

        fetchJobs();
    }, [employerUsername, navigate]);

    const handleCopyLink = (url) => {
        navigator.clipboard.writeText(url)
            .then(() => alert("Job application link copied!"))
            .catch(err => console.error("Error copying link:", err));
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate('/login');
    };

    const handleAddJob = () => navigate('/add-job');
    const handleViewUpdateProfile = () => navigate('/update-profile');
    const handleEditJob = (jobId) => navigate(`/edit-job/${jobId}`);
    const handlePreviewAssessments = (jobId) => navigate(`/assessments/${jobId}`);
    const handleViewResults = (jobId) => navigate(`/results/${jobId}`);

    const handleRemoveJob = async (jobId) => {
        if (window.confirm('Are you sure you want to delete this job? This action cannot be undone.')) {
            try {
                await axios.delete(`http://127.0.0.1:5000/jobs/${jobId}`);
                setJobs(jobs.filter((job) => job.id !== jobId));
                alert('Job removed successfully.');
            } catch (error) {
                console.error('Error removing job:', error);
                alert('Failed to remove the job. Please try again.');
            }
        }
    };

    if (loading) return <p className="loading-text">Loading jobs... Please wait.</p>;

    return (
        <div className="dashboard-container">
            <h2 className="dashboard-header">{companyName ? `${companyName}'s Dashboard` : 'Dashboard'}</h2>

            {/* Action Buttons */}
            <div className="dashboard-actions">
                <button onClick={handleAddJob} className="action-btn add-btn">+ Add Job</button>
                <button onClick={handleViewUpdateProfile} className="action-btn profile-btn">View/Update Profile</button>
            </div>

            {/* Job Table */}
            {jobs.length > 0 ? (
                <table className="job-table">
                    <thead>
                        <tr>
                            <th>Job ID</th>
                            <th>Title</th>
                            <th>Summary</th>
                            <th>Days to Expiration</th>
                            <th>Applicants</th>
                            <th>% Interviews Completed</th>
                            <th>Application Link</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {jobs.map((job) => (
                            <tr key={job.id}>
                                <td>{job.id}</td>
                                <td>{job.title}</td>
                                <td title={job.description}>{job.description.substring(0, 50)}...</td>
                                <td>{job.timeToExpiration} days</td>
                                <td>{job.totalApplicants}</td>
                                <td>{job.interviewCompletionRate}%</td>
                                <td className="application-link">
                                    <input type="text" value={job.application_url} readOnly className="job-url-input" />
                                    <button className="copy-btn" onClick={() => handleCopyLink(job.application_url)}>📋 Copy</button>
                                </td>
                                <td className="job-actions">
                                    <button onClick={() => handleEditJob(job.id)} className="edit-btn">✏️ Edit</button>
                                    <button onClick={() => handleRemoveJob(job.id)} className="delete-btn">🗑 Remove</button>
                                    <button onClick={() => handlePreviewAssessments(job.id)} className="preview-btn">👀 Preview</button>
                                    <button onClick={() => handleViewResults(job.id)} className="results-btn">📊 Results</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : (
                <p className="no-jobs-text">No jobs posted yet.</p>
            )}

            <button onClick={handleLogout} className="logout-btn">Logout</button>
        </div>
    );
};

export default Dashboard;

